import React from 'react';
import '@/styles/rich-text.css';

interface RichTextRendererProps {
  content: string;
  className?: string;
  stripHtml?: boolean; // 是否剥离HTML标签（用于显示纯文本）
}

/**
 * 增强的HTML实体解码和内容清理函数
 * 处理任意深度的HTML实体转义，并可选地剥离HTML标签
 */
function decodeHTMLEntities(text: string, stripHtml: boolean = false): string {
  if (!text) return '';
  
  let decoded = text;
  
  // 迭代解码，直到不再有变化（处理多层转义）
  let iterations = 0;
  const maxIterations = 20;
  let previousDecoded = '';
  
  // 定义所有需要解码的实体
  const entities: Record<string, string> = {
    // 基本实体
    '&amp;': '&',
    '&lt;': '<',
    '&gt;': '>',
    '&quot;': '"',
    '&apos;': "'",
    '&nbsp;': ' ',
    
    // 常见实体
    '&ndash;': '–',
    '&mdash;': '—',
    '&hellip;': '…',
    '&copy;': '©',
    '&reg;': '®',
    '&trade;': '™',
    
    // 双重转义实体
    '&amp;amp;': '&amp;',
    '&amp;lt;': '&lt;',
    '&amp;gt;': '&gt;',
    '&amp;quot;': '&quot;',
    '&amp;apos;': '&apos;',
    '&amp;nbsp;': '&nbsp;',
    
    // 三重转义实体
    '&amp;amp;amp;': '&amp;amp;',
    '&amp;amp;lt;': '&amp;lt;',
    '&amp;amp;gt;': '&amp;gt;',
    
    // HTML特殊字符的实体形式
    '&#39;': "'",
    '&#x27;': "'",
    '&#x2F;': "/",
  };
  
  while (iterations < maxIterations && previousDecoded !== decoded) {
    previousDecoded = decoded;
    
    // 1. 先处理所有已知的命名实体（按长度降序排列，避免部分匹配）
    const sortedEntities = Object.entries(entities).sort((a, b) => b[0].length - a[0].length);
    for (const [entity, char] of sortedEntities) {
      // 使用精确匹配
      const regex = new RegExp(entity.replace(/[&;]/g, '\\$&'), 'g');
      decoded = decoded.replace(regex, char);
    }
    
    // 2. 处理数字形式的实体 (&#60; = <, &#62; = >)
    decoded = decoded.replace(/&#(\d+);/g, (_, num) => {
      const code = parseInt(num, 10);
      if (code >= 32 && code <= 126) {
        return String.fromCharCode(code);
      }
      return _;
    });
    
    // 3. 处理十六进制形式的实体 (&#x3C; = <, &#x3E; = >)
    decoded = decoded.replace(/&#x([0-9A-Fa-f]+);/g, (_, hex) => {
      const code = parseInt(hex, 16);
      if (code >= 32 && code <= 126) {
        return String.fromCharCode(code);
      }
      return _;
    });
    
    iterations++;
  }
  
  // 4. 最后尝试解码任何剩余的通用HTML实体
  decoded = decoded.replace(/&(\w+);/g, (match, entity) => {
    const knownEntities: Record<string, string> = {
      'amp': '&', 'lt': '<', 'gt': '>', 'quot': '"',
      'apos': "'", 'nbsp': ' ', 'ndash': '–', 'mdash': '—',
      'hellip': '…', 'copy': '©', 'reg': '®', 'trade': '™',
      'bull': '•', 'lsquo': '‘', 'rsquo': '’',
      'ldquo': '"', 'rdquo': '"', 'laquo': '«', 'raquo': '»',
    };
    return knownEntities[entity.toLowerCase()] || match;
  });
  
  // 5. 如果需要，剥离所有HTML标签
  if (stripHtml) {
    // 移除所有HTML标签
    decoded = decoded.replace(/<[^>]*>/g, '');
    // 清理多余的空白字符
    decoded = decoded.replace(/\s+/g, ' ').trim();
  }
  
  return decoded;
}

export default function RichTextRenderer({ content, className = '', stripHtml = false }: RichTextRendererProps) {
  if (!content) {
    return null;
  }

  // 解码HTML实体，并可选地剥离HTML标签
  const decodedContent = decodeHTMLEntities(content, stripHtml);

  // 如果需要剥离HTML，直接返回纯文本
  if (stripHtml) {
    return (
      <div className={`rich-text-content rich-text-plain ${className}`}>
        {decodedContent}
      </div>
    );
  }

  // 否则返回HTML渲染
  return (
    <div 
      className={`rich-text-content ${className}`}
      dangerouslySetInnerHTML={{ __html: decodedContent }}
    />
  );
}

// Styles for rich text content - 直接导出
export const richTextStyles = `
.rich-text-content {
  font-size: 0.875rem;
  line-height: 1.75;
  color: #0A2540;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

.rich-text-content * {
  max-width: 100%;
  box-sizing: border-box;
}

.rich-text-content h1 {
  font-size: 1.875rem;
  font-weight: 700;
  margin-top: 1.5rem;
  margin-bottom: 0.75rem;
  line-height: 1.2;
  color: #0A2540;
}

.rich-text-content h2 {
  font-size: 1.5rem;
  font-weight: 600;
  margin-top: 1.25rem;
  margin-bottom: 0.625rem;
  line-height: 1.3;
  color: #0A2540;
}

.rich-text-content h3 {
  font-size: 1.25rem;
  font-weight: 600;
  margin-top: 1rem;
  margin-bottom: 0.5rem;
  line-height: 1.4;
  color: #0A2540;
}

.rich-text-content p {
  margin-bottom: 1rem;
  text-align: justify;
}

.rich-text-content p:last-child {
  margin-bottom: 0;
}

.rich-text-content strong {
  font-weight: 600;
  color: #0A2540;
}

.rich-text-content em {
  font-style: italic;
}

.rich-text-content s {
  text-decoration: line-through;
  opacity: 0.7;
}

.rich-text-content ul,
.rich-text-content ol {
  list-style-position: outside;
  padding-left: 1.5rem;
  margin-bottom: 1rem;
}

.rich-text-content ul {
  list-style-type: disc;
}

.rich-text-content ol {
  list-style-type: decimal;
}

.rich-text-content li {
  margin-bottom: 0.5rem;
}

.rich-text-content blockquote {
  border-left: 4px solid #2C7DA0;
  margin: 1rem 0;
  font-style: italic;
  color: #0A2540;
  opacity: 0.8;
  background: linear-gradient(to right, rgba(44, 125, 160, 0.05), transparent);
  padding: 1rem;
  border-radius: 0 8px 8px 0;
}

.rich-text-content pre {
  background: #F7F9FC;
  padding: 1rem;
  border-radius: 8px;
  overflow-x: auto;
  font-family: 'Courier New', monospace;
  font-size: 0.875rem;
  margin-bottom: 1rem;
  border: 1px solid #E5E7EB;
}

.rich-text-content code {
  background: #F3F4F6;
  padding: 0.125rem 0.375rem;
  border-radius: 4px;
  font-family: 'Courier New', monospace;
  font-size: 0.875rem;
  color: #0A2540;
}

.rich-text-content pre code {
  background: transparent;
  padding: 0;
}

.rich-text-content hr {
  border: none;
  border-top: 2px solid #E5E7EB;
  margin: 1.5rem 0;
}

.rich-text-content a {
  color: #2C7DA0;
  text-decoration: underline;
  transition: color 0.2s;
}

.rich-text-content a:hover {
  color: #0A2540;
}
`;
